import classNames from 'classnames'
import { KeyboardEvent, RefObject } from 'react'

import { IForm } from 'core-components/classes/models/IForm'
import { DropdownArrowWithContext } from 'core-components/library/dropdown'
import { IInputProps, Input, InputDisabledWrapper } from 'core-components/library/inputs'
import { RemoveButton } from 'core-components/library/multiSelect'
import { ItemsLine } from 'core-components/library/multiSelect/ItemsLine'
import { ICommonMultiselect } from 'core-components/library/multiSelect/models/ICommonMultiselect'
import { IItemsLine } from 'core-components/library/multiSelect/models/IItemsLine'
import { ValidateInput } from 'core-components/library/validation'

import styles from './MultiSelectInput.module.scss'

interface IBaseMultiselect<T = IForm, R = string | number> {
  classes?: NonNullable<ICommonMultiselect<T>['classes']>['inputClasses']
  onChange: ICommonMultiselect<T>['onChange']
  source: ICommonMultiselect<T, R>['source']
  disabled: ICommonMultiselect['disabled']
  caption: ICommonMultiselect['caption']
  label: ICommonMultiselect['label']
  placeholder: ICommonMultiselect['placeholder']
  useDrag: ICommonMultiselect['useDrag']
  prepareInputConfig: ICommonMultiselect['prepareInputConfig']
  checkFn: ICommonMultiselect['checkFn']
  valueDependencies?: ICommonMultiselect['valueDependencies']
  ValidateInputComponent: ICommonMultiselect['ValidateInputComponent']
  InputComponent: ICommonMultiselect['InputComponent']
  itemTemplate: ICommonMultiselect['itemTemplate']
  onKeyPress: IInputProps['onKeyPress']

  beforeRemoveHandler?: IItemsLine['beforeRemoveHandler']

  isPopupOpened: boolean
  currentValue: IForm[]
  setCurrentValue: (newValues: IForm[]) => void
  items: T[]
  setShowSelectedItems: (newValue: boolean) => void
  onChangeInputValueHandler?: (text: string) => void
  onDeleteValue: (delValue: string, moveFocusToInput: boolean) => void
  inputted: string
  setInputted: (newValue: string) => void
  inputRef: RefObject<HTMLInputElement | null>
  validatorValue: string
}

export const MultiSelectInput = <T, R>({
  classes,
  onChange,
  disabled,
  caption,
  label,
  placeholder,
  useDrag,
  source,
  prepareInputConfig,
  isPopupOpened,
  currentValue,
  setCurrentValue,
  items,
  itemTemplate,
  onKeyPress,
  setShowSelectedItems,
  onChangeInputValueHandler,
  onDeleteValue,
  inputted,
  setInputted,
  inputRef,
  checkFn,
  valueDependencies,
  validatorValue,
  beforeRemoveHandler,
  InputComponent = Input,
  ValidateInputComponent = ValidateInput,
}: IBaseMultiselect<T, R>) => {
  const clearAll = () => {
    inputRef.current?.focus()
    setInputted('')
    setCurrentValue([])
    onChange([], items)
  }

  const keyHandler = (e: KeyboardEvent<HTMLInputElement>) => {
    e.key === 'Backspace' &&
      currentValue.length &&
      !inputted &&
      onDeleteValue(currentValue[currentValue.length - 1][source.keyField], true)
  }

  const onChangeInputValue = (text: string) => {
    setInputted(text)
    if (onChangeInputValueHandler) {
      onChangeInputValueHandler(text)
    }
  }

  const inputProps = {
    disabled,
    label,
    placeholder,
    classes: {
      ...classes,
      leftSide: classNames(styles.adornmentLeft, classes?.leftSide),
    },
    autocompleteOff: true,
    onKeyDown: keyHandler,
    onKeyPress,
    value: inputted,
    onChange: (text: string) => {
      onChangeInputValue(text)
    },
    ref: inputRef,
    onFocus: () => setShowSelectedItems(false),
    startContent: currentValue.length ? (
      <ItemsLine
        beforeRemoveHandler={beforeRemoveHandler}
        useDrag={useDrag}
        caption={caption}
        itemTemplate={itemTemplate}
        onChange={(newValues, newItems) => onChange(newValues, newItems as T[])}
        className={styles.labels}
        compact
        disabled={disabled}
        value={currentValue}
        onDeleteValue={(value) => onDeleteValue(value[source.keyField], true)}
        moreButton={{
          visible: true,
          onFocus: () => setShowSelectedItems(true),
          onBlur: () => setShowSelectedItems(false),
        }}
      />
    ) : undefined,
    endContent: currentValue.length ? (
      <RemoveButton
        disabled={disabled}
        onClick={() => {
          clearAll()
        }}
        color={'gray'}
        __testId={'multiSelectInputClearAll'}
      />
    ) : (
      <InputDisabledWrapper disabled={disabled}>
        <DropdownArrowWithContext state={isPopupOpened ? 'up' : 'down'} />
      </InputDisabledWrapper>
    ),
  }

  if (prepareInputConfig) {
    prepareInputConfig(inputProps)
  }

  if (checkFn) {
    return (
      <ValidateInputComponent
        {...inputProps}
        classes={{ inputClasses: inputProps.classes }}
        validatorValue={validatorValue}
        checkFn={checkFn}
        valueDependencies={valueDependencies}
      />
    )
  }

  return <InputComponent {...inputProps} />
}
